import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import Layout from '@/components/Layout';
import { toast } from 'sonner';
import { Building2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function EmployerRegister() {
  const [user, setUser] = useState(null);
  const [regions, setRegions] = useState([]);
  const [formData, setFormData] = useState({
    company_name: '',
    company_description: '',
    website: '',
    region_id: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, regionsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/regions`)
        ]);
        setUser(userRes.data);
        setRegions(regionsRes.data);
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/employer/register`,
        formData,
        { withCredentials: true }
      );
      
      toast.success('Registration submitted! Awaiting regional admin approval.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-10 h-10 text-brand" strokeWidth={1.5} />
            <div>
              <h1 className="text-4xl font-bold text-slate-900">Become an Employer</h1>
              <p className="text-slate-600 mt-1">Connect with qualified veteran candidates</p>
            </div>
          </div>
        </div>

        <Card className="p-8 border border-slate-200 shadow-sm" data-testid="employer-register-form">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="company_name" className="text-slate-900 font-medium">Company Name *</Label>
              <Input
                data-testid="company-name-input"
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                className="h-12 mt-2"
                required
              />
            </div>

            <div>
              <Label htmlFor="company_description" className="text-slate-900 font-medium">Company Description *</Label>
              <Textarea
                data-testid="company-description-textarea"
                id="company_description"
                value={formData.company_description}
                onChange={(e) => setFormData({...formData, company_description: e.target.value})}
                className="mt-2 min-h-[120px]"
                required
              />
            </div>

            <div>
              <Label htmlFor="website" className="text-slate-900 font-medium">Website</Label>
              <Input
                data-testid="website-input"
                id="website"
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                className="h-12 mt-2"
                placeholder="https://example.com"
              />
            </div>

            <div>
              <Label htmlFor="region_id" className="text-slate-900 font-medium">Region *</Label>
              <select
                data-testid="region-select"
                id="region_id"
                value={formData.region_id}
                onChange={(e) => setFormData({...formData, region_id: e.target.value})}
                className="flex h-12 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm mt-2"
                required
              >
                <option value="">Select Region</option>
                {regions.map(region => (
                  <option key={region.region_id} value={region.region_id}>
                    {region.name}, {region.state}
                  </option>
                ))}
              </select>
            </div>

            <div className="bg-brand-light border border-brand/20 rounded-md p-4">
              <p className="text-sm text-brand">
                <strong>Next Steps:</strong> After submission, your regional admin will review your application. You'll be notified once approved.
              </p>
            </div>

            <Button
              data-testid="submit-employer-register-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-brand hover:bg-brand-hover text-white"
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </Button>
          </form>
        </Card>
      </div>
    </Layout>
  );
}

export default EmployerRegister;